import { useQuery } from '@apollo/client';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HStack,
  Icon,
  Input,
  LoadingIndicator,
  NewButton,
  Paragraph,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  VStack,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { tenantNameFragment } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import { useAddUserToTenant } from '../$tenantId/users/hooks/useAddUserToTenant';

import { usersTenantsFragment } from './TenantActionsColumn';

const getAppointmentTenantInfoQuery = graphql(
  `
    query getAppointmentTenantInfo($appointmentId: uuid!) {
      appointments(where: { id: { _eq: $appointmentId } }, limit: 1) {
        __typename
        id
        tenantId
        tenant {
          ...TenantName
        }
      }
    }
  `,
  [tenantNameFragment],
);

export const QuickProvisionDialog = () => {
  const { isDialogOpen, setIsDialogOpen } = useDialog();

  const [appointmentId, setAppointmentId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const goToAppointmentId = searchParams.get('goToAppointmentId');

  const apiClient = useApiClient();
  const navigate = useNavigate();
  const userId = useUserIdFromSession();

  // Search Param Handling

  useEffect(() => {
    if (goToAppointmentId) {
      setAppointmentId(goToAppointmentId);
      setIsDialogOpen(true);
      setSearchParams((prev) => {
        prev.delete('goToAppointmentId');
        return prev;
      });
    }
  }, [goToAppointmentId, setIsDialogOpen, setSearchParams]);

  // Appointment & Tenant Data

  const usersTenants = useCompleteFragment({
    fragment: usersTenantsFragment,
    key: { id: userId },
    strict: false,
  });

  const currentTenantCount = usersTenants?.tenants?.length ?? 0;
  const maxTenantLimit = 20;
  const hasReachedTenantLimit = currentTenantCount >= maxTenantLimit;
  const isValidUuid = z.string().uuid().safeParse(appointmentId).success;

  const { loading: isLoading } = useQuery(getAppointmentTenantInfoQuery, {
    variables: { appointmentId },
    skip: !isValidUuid,
    onCompleted: (data) => {
      const appointment = data?.appointments?.[0];
      const tenantId = appointment?.tenantId;
      const tenantName = appointment?.tenant?.name;
      const isProvisioned =
        tenantId &&
        (usersTenants?.tenants ?? []).some(
          (tenant) => tenant.tenantId === tenantId,
        );

      if (!tenantId || !tenantName) {
        setErrorMessage(
          'Appointment details could not be loaded. Please try again!',
        );
        return;
      }

      if (isProvisioned) {
        navigate(
          `/admin/organizations/${tenantId}/appointments/${appointmentId}`,
        );
        return;
      }

      setTenantId(tenantId);
      setTenantName(tenantName);
    },
    onError: () => {
      setErrorMessage(
        'Appointment details could not be loaded. Please try again!',
      );
    },
  });

  // Provisioning

  const addUserToTenant = useAddUserToTenant(tenantId ?? '', async () => {
    if (!tenantId) {
      return;
    }

    cacheUtils.updateFragment(
      {
        fragment: usersTenantsFragment,
        key: { id: userId },
      },
      (existing) => {
        if (!existing) {
          return existing;
        }

        return produce(existing, (draft) => {
          draft.tenants.push({
            tenantId,
            __typename: 'TenantUsers' as const,
            userId,
            role: 'tenant-admin',
          });
        });
      },
    );

    await apiClient.auth.createTenantSession({ body: { tenantId } });
  });

  // Handlers

  const handleDialogChange = (open: boolean) => {
    if (!isDialogOpen && open) {
      setAppointmentId('');
      setErrorMessage(null);
      setIsProvisioning(false);
      setTenantId(null);
      setTenantName(null);
    }
    setIsDialogOpen(open);
  };

  const handleProvision = () => {
    if (hasReachedTenantLimit || !tenantId) {
      return;
    }

    setIsProvisioning(true);
    addUserToTenant({
      variables: { tenantId, userId, role: 'tenant-admin' },
      onCompleted: async () => {
        await apiClient.auth.refreshAllowedTenants();
        navigate(
          `/admin/organizations/${tenantId}/appointments/${appointmentId}`,
        );
      },
      onError: (error) => {
        setIsProvisioning(false);
        setErrorMessage(`Failed to provision tenant: ${error.message}`);
      },
    });
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAppointmentId(e.target.value);
    setErrorMessage(null);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <NewButton
          icon={{ name: 'FileEdit' }}
          text="Go to Appointment..."
          type="outline"
        />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="mb-1 space-y-2">
          <DialogTitle>Go to Appointment</DialogTitle>
          <DialogDescription>
            Navigate to any appointment quickly by providing its ID below:
          </DialogDescription>
        </DialogHeader>
        <VStack align="stretch" gap={6}>
          <div className="relative">
            <Input
              className="pr-10"
              disabled={isLoading || isProvisioning}
              onChange={handleValueChange}
              placeholder="Appointment ID"
              value={appointmentId}
            />
            <div className="absolute right-0 top-0 size-10">
              {isLoading && <LoadingIndicator size="xs" />}
            </div>
          </div>
          {errorMessage && (
            <HStack
              className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle p-3 text-text-negative-subtle"
              gap={3}
            >
              <Icon name="AlertCircle" size="xs" />
              <Paragraph color="inherit" size="md" weight="regular">
                {errorMessage}
              </Paragraph>
            </HStack>
          )}
          {!errorMessage && tenantId && tenantName && (
            <HStack
              className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-text-warning-subtle"
              gap={3}
            >
              <Icon name="AlertTriangle" size="xs" />
              <VStack align="stretch" gap={0}>
                <Paragraph color="inherit" size="md" weight="regular">
                  You don't have access to this organization:
                </Paragraph>
                <Paragraph color="inherit" size="md" weight="medium">
                  {tenantName}
                </Paragraph>
              </VStack>
              <Tooltip>
                <TooltipTrigger asChild>
                  <NewButton
                    disabled={hasReachedTenantLimit}
                    loading={isProvisioning}
                    text="Provision"
                    onClick={handleProvision}
                  />
                </TooltipTrigger>
                {hasReachedTenantLimit && (
                  <TooltipContent>
                    You reached the maximum number of provisioned tenants
                    allowed.
                  </TooltipContent>
                )}
              </Tooltip>
            </HStack>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  );
};
